import React, { useEffect, useState } from 'react';
import {
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { buttonsAvailable } from './buttonsAvailable';
import { ClientConfigState } from 'services/redux/clientConfigSlice';
import { getReceiverCompanyProducts } from 'lib/api';
import { setClientCompanyTypes } from 'services/redux/clientCompanyTypesSlice';

const activeFontColor = '#0F23DF';
const activeWeight = '300';

const useStyles = makeStyles(() =>
  createStyles({
    sidebar: {
      minWidth: 210, // Adjust the width as needed
      height: '90%', // Set the sidebar to full height
      backgroundColor: 'ffffff', // Change the background color
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRight: '1px solid #eaecf0',
    },
    logo: {
      padding: '20px 0px 0px',
      textAlign: 'center',
      width: '100%',
      height: '52px',
      borderBottom: '1px solid #eaecf0',
    },
    upItems: {
      width: '100%',
    },
    navList: {
      width: '80%',
    },
    avatarSection: {
      cursor: 'pointer',
      width: '100%',
      padding: '10px',
      overflowX: 'scroll',
      borderTop: '1px solid #eaecf0',
      display: 'flex',
      alignItems: 'center',
    },
    avatarSectionActive: {
      borderTop: `1px solid ${activeFontColor}`,
    },
    avatarActive: {
      color: `${activeFontColor} !important`,
    },
    avatar: {
      width: 45, // Adjust the size of the avatar as needed
      height: 45, // Match the size of the avatar to the text
      margin: '0px 12px 0px 0px',
    },
  }),
);

const SideBar: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const currentPath = window.location.pathname;
  const [activeItem, setActiveItem] = useState('home');

  const expressConfig = useSelector(
    (state: { clientConfiguration: ClientConfigState }) =>
      state.clientConfiguration.configs,
  );

  const clientCompanyTypes = useSelector(
    (state: { clientCompanyTypes: { types: any[] } }) =>
      state.clientCompanyTypes.types,
  );

  const StyledListItemButton = styled(ListItemButton)(
    ({ active }: { active: boolean }) => ({
      margin: '6px 8px',
      borderRadius: '16px',
      '& .MuiListItemText-primary': {
        fontSize: 17,
      },
      '&:hover': {
        backgroundColor: '#efefef',
        '& .MuiListItemIcon-root': {
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
        '& .MuiListItemText-primary': {
          fontWeight: activeWeight,
        },
      },
      ...(active && {
        backgroundColor: '#efefef',
        '& .MuiListItemText-primary': {
          fontWeight: activeWeight,
          fontSize: 17,
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
        '& .MuiListItemIcon-root': {
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
      }),
    }),
  );

  const StyledListItemIcon = styled(ListItemIcon)(
    ({ active }: { active: boolean }) => ({
      margin: '0px 10px 0px 0px',
      minWidth: 0,
      '&:hover': {
        color: expressConfig?.pruffConnect_styles_mainColor,
      },
      ...(active && {
        color: expressConfig?.pruffConnect_styles_mainColor,
      }),
    }),
  );

  const handleNavigation = (id: string) => {
    setActiveItem(id);
    window.location.href = `/${id}`;
  };

  const fetchClientCompanyTypes = async () => {
    try {
      const response = await getReceiverCompanyProducts(dispatch);
      dispatch(setClientCompanyTypes(response));
    } catch (error) {
      console.error('Error fetching client company types:', error);
    }
  };

  const setNavButtons = () => {
    const navButtons: any = [];
    navButtons.push(buttonsAvailable['home']);
    if (clientCompanyTypes?.some((product) => product.typeId === 'factoring')) {
      navButtons.push(buttonsAvailable['factoring']);
    }
    if (clientCompanyTypes?.some((product) => product.typeId === 'sgr')) {
      navButtons.push(buttonsAvailable['sgr']);
    }
    if (clientCompanyTypes?.some((product) => product.typeId === 'credit')) {
      navButtons.push(buttonsAvailable['credit']);
    }
    navButtons.push(buttonsAvailable['settings']);
    const uniqueNavButtons = Array.from(
      new Set(navButtons.map((a: any) => a.label)),
    ).map((label) => {
      return navButtons.find((a: any) => a.label === label);
    });
    return uniqueNavButtons;
  };

  const navButtons = setNavButtons();

  useEffect(() => {
    const path = currentPath.split('/')[1] || 'home';
    setActiveItem(path);
  }, [currentPath]);

  useEffect(() => {
    fetchClientCompanyTypes();
  }, []);

  return (
    <div className={classes.sidebar}>
      <div className={classes.upItems}>
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            paddingTop: 2,
          }}
          component="nav"
          aria-labelledby="nested-list-subheader"
        >
          {navButtons.map((button, index) => {
            const active = button.id === activeItem;
            return (
              <div key={index} style={{ position: 'relative' }}>
                <div style={{ padding: '0px 0px' }}>
                  <StyledListItemButton
                    key={index}
                    active={active}
                    onClick={() => handleNavigation(button.id)}
                  >
                    <StyledListItemIcon active={active}>
                      {button.icon}
                    </StyledListItemIcon>
                    <ListItemText primary={button.label} />
                  </StyledListItemButton>
                </div>
              </div>
            );
          })}
        </List>
      </div>
    </div>
  );
};

export default SideBar;
